import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem,
  Skeleton, Snackbar, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { BsLink45Deg, BsPencil, BsPersonPlus, BsTrash } from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';
import Fiche from '../Expert/Fiche';
import { themeExpert } from '../../theme/expert';

const GENRES = ['Monsieur', 'Madame', 'Autre'];

/**
 * Les champs d'un intervenant.
 *
 * `role1` et `role2` restent deux textes libres, lus et écrits tels quels :
 * toute la chaîne du dossier s'appuie sur eux, et les remplacer par une liste
 * fermée casserait les dossiers existants.
 */
const VIDE = {
  genre: '', name: '', firstname: '', mail: '', phone: '',
  role1: '', role2: '', reference: '', letterbox: '', number: '',
  street: '', postcode: '', city: '', bank_account: '', model: false,
};

function ListContact() {
  const { expertiseId } = useParams();

  const [intervenants, setIntervenants] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');

  const [fiche, setFiche] = useState(null);   // null = fenêtre fermée
  const [aSupprimer, setASupprimer] = useState(null);
  const [occupe, setOccupe] = useState(false);

  // Recherche dans le carnet d'adresses, à l'ajout seulement.
  const [recherche, setRecherche] = useState('');
  const [propositions, setPropositions] = useState([]);

  // Dossiers reliés : chacun garde le sien, on n'échange que ce qu'on envoie.
  const [liaisons, setLiaisons] = useState({ liaisons: [], invitations: [] });
  const [aRelier, setARelier] = useState(null);   // { intervenant, email }
  const [aRompre, setARompre] = useState(null);

  const chargerLiaisons = useCallback(() => {
    axiosInstance.get(`/expertises/${expertiseId}/liaisons/`,
                      { params: { origine: window.location.origin } })
      .then(({ data }) => setLiaisons(data))
      .catch(() => setLiaisons({ liaisons: [], invitations: [] }));
  }, [expertiseId]);

  useEffect(() => { chargerLiaisons(); }, [chargerLiaisons]);

  const liaisonDe = (personne) => liaisons.liaisons.find(
    (l) => l.intervenant === personne.id && !l.rompue_le);
  const invitationDe = (personne) => liaisons.invitations.find(
    (i) => i.intervenant === personne.id);

  const relier = async () => {
    setOccupe(true);
    try {
      await axiosInstance.post(`/expertises/${expertiseId}/liaisons/`, {
        intervenant: aRelier.intervenant.id, email: aRelier.email,
        origine: window.location.origin,
      });
      setMessage('Invitation envoyée');
      setARelier(null);
      chargerLiaisons();
      charger();
    } catch (error) {
      setErreur(error.response?.data?.detail || "L'invitation n'a pas pu partir.");
    } finally {
      setOccupe(false);
    }
  };

  const annulerInvitation = async (invitation) => {
    try {
      await axiosInstance.delete(`/liaisons/invitations/${invitation.id}/`);
      chargerLiaisons();
    } catch {
      setErreur("L'invitation n'a pas pu être annulée.");
    }
  };

  const rompre = async () => {
    setOccupe(true);
    try {
      await axiosInstance.post(`/liaisons/${aRompre.id}/rompre/`);
      setMessage('Relation rompue');
      setARompre(null);
      chargerLiaisons();
    } catch {
      setErreur("La relation n'a pas pu être rompue.");
    } finally {
      setOccupe(false);
    }
  };

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axiosInstance.get('/personne/',
                                               { params: { expertise_id: expertiseId } });
      setIntervenants(data.results || data);
      setErreur('');
    } catch {
      setErreur("Les intervenants n'ont pas pu être chargés.");
    } finally {
      setChargement(false);
    }
  }, [expertiseId]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    if (!recherche) { setPropositions([]); return undefined; }
    // Au repos de la frappe : le carnet était interrogé à chaque lettre.
    const minuterie = setTimeout(() => {
      axiosInstance.get('/persons/', { params: { name: recherche } })
        .then(({ data }) => setPropositions(data.results || data))
        .catch(() => setPropositions([]));
    }, 300);
    return () => clearTimeout(minuterie);
  }, [recherche]);

  const ouvrirAjout = () => {
    setRecherche('');
    setPropositions([]);
    setFiche({ ...VIDE });
  };

  const modifier = (champ) => (e) => setFiche((etat) => ({
    ...etat,
    [champ]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  const enregistrer = async (e) => {
    e.preventDefault();
    setOccupe(true);
    try {
      if (fiche.id) {
        await axiosInstance.patch(`/personne/${fiche.id}/`, fiche);
      } else {
        await axiosInstance.post('/personne/',
                                 { ...fiche, expertise: expertiseId });
      }
      setMessage(fiche.id ? 'Intervenant modifié' : 'Intervenant ajouté');
      setFiche(null);
      charger();
    } catch {
      setErreur("L'intervenant n'a pas pu être enregistré.");
    } finally {
      setOccupe(false);
    }
  };

  const supprimer = async () => {
    setOccupe(true);
    try {
      // La barre finale évite la redirection 301 que Django renvoie sans elle.
      await axiosInstance.delete(`/personne/${aSupprimer.id}/`);
      setMessage('Intervenant retiré');
      setASupprimer(null);
      charger();
    } catch {
      setErreur("L'intervenant n'a pas pu être retiré.");
    } finally {
      setOccupe(false);
    }
  };

  const nomComplet = (p) => [p.name, p.firstname].filter(Boolean).join(' ');

  return (
    <>
      <Fiche
        titre="Intervenants"
        explication="Qui intervient au dossier, et à quel titre."
        actions={(
          <Button variant="contained" size="small"
                  startIcon={<BsPersonPlus />} onClick={ouvrirAjout}>
            Ajouter
          </Button>
        )}
        sx={{ p: 0 }}
      >
        {erreur && <Alert severity="error" onClose={() => setErreur('')}
                          sx={{ m: 2 }}>{erreur}</Alert>}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Rôle</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Téléphone</TableCell>
                <TableCell align="right">&nbsp;</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {chargement && [...Array(3)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={5}><Skeleton height={28} /></TableCell>
                </TableRow>
              ))}

              {!chargement && intervenants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 5, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Aucun intervenant pour l'instant.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {intervenants.map((personne) => (
                <TableRow key={personne.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {nomComplet(personne)}
                    </Typography>
                    {personne.reference && (
                      <Typography variant="caption" color="text.secondary">
                        {personne.reference}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap"
                           useFlexGap>
                      {personne.role1 && <Chip size="small" color="primary"
                                               label={personne.role1} />}
                      {personne.role2 && <Chip size="small" variant="outlined"
                                               label={personne.role2} />}
                      {liaisonDe(personne) && (
                        <Chip size="small" color="success" variant="outlined"
                              icon={<BsLink45Deg />}
                              label={`dossier relié${liaisonDe(personne).reference
                                ? ` · ${liaisonDe(personne).reference}` : ''}`} />
                      )}
                      {!liaisonDe(personne) && invitationDe(personne) && (
                        <Chip size="small" color="warning" variant="outlined"
                              label="invitation envoyée" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {personne.mail ? (
                      <Box component="a" href={`mailto:${personne.mail}`}
                           sx={{ color: 'primary.main' }}>{personne.mail}</Box>
                    ) : <Box component="span"
                             sx={{ color: 'text.disabled' }}>—</Box>}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {personne.phone || <Box component="span"
                                            sx={{ color: 'text.disabled' }}>—</Box>}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {!liaisonDe(personne) && (
                      <Tooltip title="Relier à son dossier">
                        <IconButton size="small" aria-label="Relier à son dossier"
                                    onClick={() => setARelier({
                                      intervenant: personne,
                                      email: invitationDe(personne)?.email
                                        || personne.mail || '',
                                    })}>
                          <BsLink45Deg />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton size="small" aria-label="Modifier"
                                onClick={() => setFiche({ ...VIDE, ...personne })}>
                      <BsPencil />
                    </IconButton>
                    <IconButton size="small" aria-label="Retirer"
                                onClick={() => setASupprimer(personne)}>
                      <BsTrash />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Fiche>

      {(liaisons.liaisons.length > 0 || liaisons.invitations.length > 0) && (
        <Fiche titre="Dossiers reliés"
               explication="Chacun garde son dossier. Vous n'échangez que ce que vous vous envoyez, par une tâche « Partage ». L'autre partie ne voit de vous que votre référence et le nom de votre organisation."
               sx={{ mt: 3 }}>
          <Stack spacing={1.5}>
            {liaisons.liaisons.map((l) => (
              <Stack key={`l${l.id}`} direction={{ xs: 'column', sm: 'row' }}
                     spacing={1} justifyContent="space-between"
                     alignItems={{ sm: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {l.organisation}{l.reference ? ` — dossier ${l.reference}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {l.intervenant_libelle && `${l.intervenant_libelle} · `}
                    {l.rompue_le
                      ? `relation rompue le ${new Date(l.rompue_le).toLocaleDateString('fr-BE')}${l.rompue_par_moi ? '' : ' par l\'autre partie'}`
                      : `relié depuis le ${new Date(l.creee_le).toLocaleDateString('fr-BE')}`}
                  </Typography>
                </Box>
                {!l.rompue_le && (
                  <Button size="small" color="inherit" onClick={() => setARompre(l)}>
                    Rompre
                  </Button>
                )}
              </Stack>
            ))}
            {liaisons.invitations.map((i) => (
              <Stack key={`i${i.id}`} direction={{ xs: 'column', sm: 'row' }}
                     spacing={1} justifyContent="space-between"
                     alignItems={{ sm: 'center' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Invitation envoyée à {i.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    En attente, valable jusqu'au {new Date(i.expire_le).toLocaleDateString('fr-BE')}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  {i.lien && (
                    <Button size="small"
                            onClick={() => {
                              navigator.clipboard?.writeText(i.lien);
                              setMessage('Lien copié');
                            }}>
                      Copier le lien
                    </Button>
                  )}
                  <Button size="small" color="inherit"
                          onClick={() => annulerInvitation(i)}>
                    Annuler
                  </Button>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Fiche>
      )}

      {/* Les fenêtres se rendent dans un portail, hors de la coquille : le
          thème doit les y suivre. */}
      <ThemeProvider theme={themeExpert}>
        <Dialog open={fiche !== null} onClose={() => setFiche(null)}
                fullWidth maxWidth="md">
          <Box component="form" onSubmit={enregistrer} noValidate>
            <DialogTitle>
              {fiche?.id ? "Modifier l'intervenant" : 'Ajouter un intervenant'}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2.5} sx={{ pt: 1 }}>
                {/* Seulement à l'ajout : proposer une recherche en modification
                    revenait à offrir de remplacer la personne qu'on édite. */}
                {fiche && !fiche.id && (
                  <Autocomplete
                    freeSolo
                    options={propositions}
                    getOptionLabel={(o) => (typeof o === 'string' ? o
                      : `${o.name} ${o.firstname || ''}`.trim())}
                    inputValue={recherche}
                    onInputChange={(_, valeur) => setRecherche(valeur)}
                    onChange={(_, valeur) => {
                      if (valeur && typeof valeur === 'object') {
                        // L'identifiant du carnet ne doit pas suivre : on
                        // crée un intervenant du dossier, pas un doublon.
                        const { id, ...reste } = valeur;
                        setFiche({ ...VIDE, ...reste,
                                   mail: valeur.email || valeur.mail || '',
                                   letterbox: valeur.letter_box || '' });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params}
                                 label="Reprendre quelqu'un du carnet"
                                 helperText="Facultatif — les champs se remplissent seuls." />
                    )}
                  />
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField select label="Genre" value={fiche?.genre || ''}
                             onChange={modifier('genre')}
                             sx={{ minWidth: 140 }}>
                    <MenuItem value=""><em>Aucun</em></MenuItem>
                    {GENRES.map((g) => (
                      <MenuItem key={g} value={g}>{g}</MenuItem>
                    ))}
                  </TextField>
                  <TextField label="Nom" value={fiche?.name || ''}
                             onChange={modifier('name')} required />
                  <TextField label="Prénom" value={fiche?.firstname || ''}
                             onChange={modifier('firstname')} />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField label="Email" type="email"
                             value={fiche?.mail || ''}
                             onChange={modifier('mail')} />
                  <TextField label="Téléphone" value={fiche?.phone || ''}
                             onChange={modifier('phone')} />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField label="Rôle principal" value={fiche?.role1 || ''}
                             onChange={modifier('role1')}
                             helperText="Assuré, courtier, entrepreneur…" />
                  <TextField label="Rôle secondaire" value={fiche?.role2 || ''}
                             onChange={modifier('role2')} helperText=" " />
                </Stack>

                <TextField label="Référence" value={fiche?.reference || ''}
                           onChange={modifier('reference')}
                           helperText="La référence de cette personne pour ce dossier." />

                <Box>
                  <Typography variant="body2" color="text.secondary"
                              sx={{ mb: 1 }}>Adresse</Typography>
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField label="Boîte" value={fiche?.letterbox || ''}
                                 onChange={modifier('letterbox')}
                                 sx={{ maxWidth: { sm: 120 } }} />
                      <TextField label="Numéro" value={fiche?.number || ''}
                                 onChange={modifier('number')}
                                 sx={{ maxWidth: { sm: 120 } }} />
                      <TextField label="Rue" value={fiche?.street || ''}
                                 onChange={modifier('street')} />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField label="Code postal"
                                 value={fiche?.postcode || ''}
                                 onChange={modifier('postcode')}
                                 sx={{ maxWidth: { sm: 180 } }} />
                      <TextField label="Ville" value={fiche?.city || ''}
                                 onChange={modifier('city')} />
                    </Stack>
                  </Stack>
                </Box>

                <TextField label="Compte bancaire"
                           value={fiche?.bank_account || ''}
                           onChange={modifier('bank_account')}
                           helperText="Pour le règlement de l'indemnité." />

                <FormControlLabel
                  control={<Checkbox checked={!!fiche?.model}
                                     onChange={modifier('model')} />}
                  label="Garder dans le carnet, pour les dossiers suivants"
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setFiche(null)} disabled={occupe}>
                Annuler
              </Button>
              <Button type="submit" variant="contained"
                      disabled={occupe || !fiche?.name?.trim()}>
                {occupe ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>

        <Dialog open={aRelier !== null} onClose={() => setARelier(null)}
                fullWidth maxWidth="sm">
          <DialogTitle>Relier à son dossier</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2">
                {aRelier && nomComplet(aRelier.intervenant)} recevra un lien pour
                relier son propre dossier au vôtre, ou en ouvrir un. Vous
                pourrez ensuite lui envoyer des pièces par une tâche
                « Partage », sans courriel.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ce n'est pas un accès à votre dossier : chacun garde le sien.
                Il verra votre référence et le nom de votre organisation ; s'il
                ouvre un dossier, il reprendra la date et l'adresse du sinistre.
              </Typography>
              <TextField label="Son adresse électronique" type="email" required
                         value={aRelier?.email || ''}
                         onChange={(e) => setARelier((x) => ({ ...x, email: e.target.value }))}
                         helperText="Le lien ne s'ouvre qu'avec un compte à cette adresse. Il est valable 7 jours." />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setARelier(null)} disabled={occupe}>Annuler</Button>
            <Button variant="contained" onClick={relier}
                    disabled={occupe || !aRelier?.email?.includes('@')}>
              {occupe ? 'Envoi…' : "Envoyer l'invitation"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={aRompre !== null} onClose={() => setARompre(null)}>
          <DialogTitle>Rompre la relation ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Vous ne pourrez plus vous envoyer de pièces avec {aRompre?.organisation}.
              Ce qui a déjà été envoyé reste chez celui qui l'a reçu.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setARompre(null)} disabled={occupe}>Annuler</Button>
            <Button onClick={rompre} color="error" variant="contained" disabled={occupe}>
              Rompre
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={aSupprimer !== null} onClose={() => setASupprimer(null)}>
          <DialogTitle>Retirer cet intervenant ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {aSupprimer && nomComplet(aSupprimer)} sera retiré de ce dossier.
              C'est définitif.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setASupprimer(null)} disabled={occupe}>
              Annuler
            </Button>
            <Button onClick={supprimer} color="error" variant="contained"
                    disabled={occupe}>
              Retirer
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={3000}
        onClose={() => setMessage('')}
        message={message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}

export default ListContact;
